// Contact Messages Service - Handles saving and retrieving contact messages
// Uses Supabase as primary storage with localStorage fallback
import { supabase } from '../config/supabase';

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  purpose: string;
  message: string;
  timestamp: string;
  read: boolean;
}

/**
 * Save a contact message to both Supabase and localStorage
 * This ensures the message persists permanently in the database
 * and is also available locally for offline access
 */
export const saveContactMessage = async (messageData: Omit<ContactMessage, 'id' | 'timestamp' | 'read'>): Promise<ContactMessage | null> => {
  try {
    const newMessage: ContactMessage = {
      id: Date.now().toString(),
      ...messageData,
      timestamp: new Date().toISOString(),
      read: false
    };

    console.log('💾 Saving message to contact service:', newMessage);

    // Try to save to Supabase (primary storage)
    let savedToSupabase = false;
    try {
      console.log('📤 Attempting to save to Supabase...');
      const { data, error: supabaseError } = await supabase
        .from('contact_messages')
        .insert([{
          id: newMessage.id,
          name: newMessage.name,
          email: newMessage.email,
          purpose: newMessage.purpose,
          message: newMessage.message,
          timestamp: newMessage.timestamp,
          read: newMessage.read
        }]);

      if (supabaseError) {
        console.warn('⚠️ Supabase save failed:', supabaseError.code, supabaseError.message);
      } else {
        console.log('✅ Message saved to Supabase database');
        savedToSupabase = true;
      }
    } catch (supabaseErr) {
      console.warn('⚠️ Supabase connection error:', supabaseErr);
    }

    // Always save to localStorage as fallback/cache
    let savedToLocalStorage = false;
    try {
      const messages = JSON.parse(localStorage.getItem('contact_messages') || '[]');
      messages.push(newMessage);
      localStorage.setItem('contact_messages', JSON.stringify(messages));
      console.log('✅ Message saved to localStorage. Total messages:', messages.length);
      savedToLocalStorage = true;
    } catch (localStorageErr) {
      console.error('❌ Failed to save to localStorage:', localStorageErr);
    }

    if (savedToSupabase || savedToLocalStorage) {
      console.log('✨ Message saved successfully (Supabase:', savedToSupabase, ', LocalStorage:', savedToLocalStorage, ')');
      return newMessage;
    } else {
      console.error('❌ Failed to save message to any storage');
      return null;
    }
  } catch (err) {
    console.error('❌ Critical error in saveContactMessage:', err);
    return null;
  }
};

/**
 * Load contact messages from Supabase
 * Falls back to localStorage if Supabase is unavailable
 */
export const loadContactMessages = async (): Promise<ContactMessage[]> => {
  try {
    console.log('📧 Loading messages from Supabase...');
    
    // Try Supabase first (primary source of truth)
    const { data, error } = await supabase
      .from('contact_messages')
      .select('*')
      .order('timestamp', { ascending: false });

    if (error) {
      console.warn('⚠️ Supabase load failed:', error.code, error.message);
      
      // Check if it's a permission error
      if (error.code === 'PGRST116' || error.message?.includes('permission denied')) {
        console.warn('🔒 Permission denied - likely RLS policy issue. Ensure table exists and policies are correct.');
        console.warn('💡 Tip: Run the SQL migration in Supabase to fix this.');
      }
      
      console.log('📦 Falling back to localStorage...');
      return loadFromLocalStorage();
    }

    if (data && data.length > 0) {
      console.log(`✅ Loaded ${data.length} messages from Supabase`);
      
      // Sync to localStorage for offline access
      try {
        localStorage.setItem('contact_messages', JSON.stringify(data));
        console.log('✅ Synced to localStorage');
      } catch (e) {
        console.warn('Failed to sync to localStorage:', e);
      }
      
      return data;
    }

    console.log('ℹ️ No messages in Supabase, checking localStorage...');
    // Supabase returned empty, try localStorage
    return loadFromLocalStorage();
  } catch (err) {
    console.error('❌ Error loading messages:', err);
    return loadFromLocalStorage();
  }
};

/**
 * Load messages from localStorage (fallback)
 */
const loadFromLocalStorage = (): ContactMessage[] => {
  try {
    const stored = localStorage.getItem('contact_messages');
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log(`📦 Loaded ${parsed.length} messages from localStorage`);
      return parsed.sort((a: ContactMessage, b: ContactMessage) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    }
    console.log('ℹ️ No messages in localStorage');
  } catch (err) {
    console.error('Failed to parse localStorage:', err);
  }
  return [];
};

/**
 * Mark a message as read in both Supabase and localStorage
 */
export const markMessageAsRead = async (messageId: string): Promise<boolean> => {
  try {
    // Update Supabase
    const { error: supabaseError } = await supabase
      .from('contact_messages')
      .update({ read: true })
      .eq('id', messageId);

    if (supabaseError) {
      console.warn('Failed to update read status in Supabase:', supabaseError);
    }

    // Update localStorage
    try {
      const messages = JSON.parse(localStorage.getItem('contact_messages') || '[]');
      const updated = messages.map((m: ContactMessage) =>
        m.id === messageId ? { ...m, read: true } : m
      );
      localStorage.setItem('contact_messages', JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to update localStorage:', e);
    }

    return true;
  } catch (err) {
    console.error('Failed to mark message as read:', err);
    return false;
  }
};

/**
 * Delete a message from both Supabase and localStorage
 */
export const deleteContactMessage = async (messageId: string): Promise<boolean> => {
  try {
    // Delete from Supabase
    const { error: supabaseError } = await supabase
      .from('contact_messages')
      .delete()
      .eq('id', messageId);

    if (supabaseError) {
      console.warn('Failed to delete from Supabase:', supabaseError);
    }

    // Delete from localStorage
    try {
      const messages = JSON.parse(localStorage.getItem('contact_messages') || '[]');
      const updated = messages.filter((m: ContactMessage) => m.id !== messageId);
      localStorage.setItem('contact_messages', JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to update localStorage:', e);
    }

    return true;
  } catch (err) {
    console.error('Failed to delete message:', err);
    return false;
  }
};

/**
 * Create the contact_messages table in Supabase if it doesn't exist
 * Run this once to set up the database
 */
export const initializeContactMessagesTable = async (): Promise<boolean> => {
  try {
    // Check if table exists by querying it
    const { data, error } = await supabase
      .from('contact_messages')
      .select('id')
      .limit(1);

    // Table exists if no "relation does not exist" error
    if (!error || !error.message.includes('relation')) {
      console.log('✅ contact_messages table exists');
      return true;
    }

    console.warn('Table does not exist. Please run the migration in Supabase SQL editor');
    return false;
  } catch (err) {
    console.error('Error checking table:', err);
    return false;
  }
};
